// See EnrollHostsDrawer.spec.md for EHD-FR-004
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { getBlueprints } from 'app/plugins/openstack/components/infrastructure/actions'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import Badge from 'core/elements/badge'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import type Theme from 'core/themes/model'
import { GET_DRIVER_CONFIG } from 'openstack/components/infrastructure/blueprint/helpers'
import type {
  Blueprint,
  StorageBackendDefinition,
} from 'openstack/components/infrastructure/blueprint/model'
import {
  getArrayAndProtocolForDriver,
  getStorageArray,
} from 'openstack/components/infrastructure/blueprint/volume-backend-configs/AddVolumeBackendConfigWizard.helpers'
import type { EnrollStepProps } from './EnrollHostsDrawer'
import StepSection from './StepSection'

interface FlattenedBackendConfig extends StorageBackendDefinition {
  name: string
  volumeType: string
}

// Same flattening as EditServerRolesModal.tsx's volumeBackendConfigs: blueprint storageBackends
// is keyed [volumeType][configName], so one card is produced per volumeType+name pair.
const flattenBackendConfigs = (blueprint: Blueprint | null): FlattenedBackendConfig[] => {
  const storageBackends = blueprint?.storageBackends || {}
  return Object.keys(storageBackends).flatMap((volumeType) =>
    Object.keys(storageBackends[volumeType]).map((name) => ({
      ...storageBackends[volumeType][name],
      name,
      volumeType,
    })),
  )
}

// Keys the blueprint wizard itself treats as passwords (type: 'password' in GET_DRIVER_CONFIG).
const PASSWORD_KEYS = new Set(
  Object.values(
    GET_DRIVER_CONFIG as Record<string, { keyValues: { key: string; type?: string }[] }>,
  )
    .flatMap(({ keyValues }) => keyValues)
    .filter((field) => field.type === 'password')
    .map((field) => field.key),
)

// EHD-FR-004, SC-003: never show a secret driver param value in clear text. Masks the wizard's
// password fields plus any key that looks secret, so custom drivers are covered too.
export const maskSecret = (key: string, value: unknown): string =>
  PASSWORD_KEYS.has(key) || /password|secret|token|key/i.test(key) ? '••••••••••' : String(value)

export default function VolumeBackendsStep({ selection, onChange }: EnrollStepProps) {
  const classes = useStyles()
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  useEffect(() => {
    let active = true
    getBlueprints()
      .then((blueprints: Blueprint[]) => {
        if (!active) return
        setBlueprint(blueprints?.[0] || null)
        setLoading(false)
      })
      .catch(() => {
        if (!active) return
        setError(true)
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const configs = useMemo(() => flattenBackendConfigs(blueprint), [blueprint])

  const toggleExpanded = (cardKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(cardKey)) {
        next.delete(cardKey)
      } else {
        next.add(cardKey)
      }
      return next
    })
  }

  const toggleBackend = (name: string, checked: boolean) => {
    const backends = checked
      ? [...selection.backends, name]
      : selection.backends.filter((backend) => backend !== name)
    onChange({ backends })
  }

  return (
    <div className={classes.root}>
      <StepSection
        title="Select Volume Backends"
        description="Selected backends are configured on every host in this enrollment (adds the Persistent Storage role). Expand a backend to see its settings."
      />
      <Text variant="body2" className={classes.sectionLabel}>
        Volume Backend Configurations
      </Text>
      {loading && (
        <Text variant="body2" className={classes.description}>
          Loading volume backend configurations...
        </Text>
      )}
      {!loading && error && (
        <Text variant="body2" className={classes.errorText}>
          Unable to load volume backend configurations.
        </Text>
      )}
      {!loading && !error && configs.length === 0 && (
        // TODO(design): empty state visual
        <Text variant="body2" className={classes.description}>
          No volume backends are configured in the cluster blueprint.
        </Text>
      )}
      {!loading &&
        !error &&
        configs.map((config) => {
          const cardKey = `${config.volumeType}-${config.name}`
          const checked = selection.backends.includes(config.name)
          const isExpanded = expanded.has(cardKey)
          const { arrayId, protocol } = getArrayAndProtocolForDriver(config.driver)
          const arrayLabel = getStorageArray(arrayId)?.label || '-'
          const paramKeys = Object.keys(config.config || {})
          const fields = [
            { label: 'Volume Backend', value: config.volumeType },
            { label: 'Driver', value: config.driver },
            { label: 'Protocol', value: protocol || '-' },
            { label: 'Array', value: arrayLabel },
          ]
          return (
            <div key={cardKey} className={clsx(classes.card, checked && classes.cardSelected)}>
              <div className={classes.cardHeader}>
                <CheckboxField
                  id={`backend-${cardKey}`}
                  label={config.name}
                  aria-label={config.name}
                  value={checked}
                  onChange={(value: boolean) => toggleBackend(config.name, value)}
                />
                <FontAwesomeIcon
                  className={classes.chevron}
                  onClick={() => toggleExpanded(cardKey)}
                  data-testid={`expand-backend-${cardKey}`}
                >
                  {isExpanded ? 'chevron-up' : 'chevron-down'}
                </FontAwesomeIcon>
              </div>
              {/* "N hosts" / "N volumes" chips were dropped: volumeBackendsPerHost (and the
                  per-host volume_backend_name list it builds) are keyed by backend/volume_backend
                  strings from the role config, not by this blueprint config's name, so hosts and
                  volumes can't be reliably counted per config from existing data. */}
              <div className={classes.cardRow}>
                {fields.map((field) => (
                  <div key={field.label} className={classes.cardField}>
                    <Text variant="body2" className={classes.label}>
                      {field.label}
                    </Text>
                    <Text variant="caption1" className={classes.value}>
                      {field.value}
                    </Text>
                  </div>
                ))}
              </div>
              {isExpanded && (
                <div className={classes.expandedSection} data-testid={`backend-details-${cardKey}`}>
                  <Text variant="body2" className={classes.label}>
                    Volume types
                  </Text>
                  <div className={classes.chips}>
                    <Badge text={config.volumeType} variant="default" />
                  </div>
                  <Text variant="body2" className={clsx(classes.label, classes.paramsLabel)}>
                    Driver parameters
                  </Text>
                  {paramKeys.length === 0 ? (
                    <Text variant="body2">No driver parameters.</Text>
                  ) : (
                    paramKeys.map((key) => (
                      <div key={key} className={classes.paramRow}>
                        <Text variant="body2">{key}</Text>
                        <Text variant="caption1" className={classes.value}>
                          {maskSecret(key, config.config[key])}
                        </Text>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )
        })}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    // Room below the last card so expanded backends don't end flush against the footer
    paddingBottom: theme.spacing(3),
  },
  description: {
    color: colors.grey[700],
  },
  errorText: {
    color: theme.palette.error.main,
  },
  // Figma: grey regular label with a line under it, then the cards
  sectionLabel: {
    color: colors.grey[500],
    fontWeight: 600,
    paddingBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    borderBottom: `1px solid ${colors.grey[200]}`,
  },
  card: {
    border: `1px solid ${colors.grey[200]}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0, 3),
  },
  // Figma: picked backend gets a light blue fill and blue border
  cardSelected: {
    borderColor: colors.blue[700],
    backgroundColor: colors.blue[100],
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${colors.grey[200]}`,
    // Bold name: CheckboxField always forces its light label weight, so set it here
    '& label': {
      fontSize: 16,
      fontWeight: 600,
    },
  },
  chevron: {
    cursor: 'pointer',
    color: colors.grey[700],
  },
  // Figma: four fields with thin dividers, lined up with the backend name (past the checkbox)
  cardRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    padding: theme.spacing(2, 0, 2, 4),
  },
  cardField: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
    padding: theme.spacing(0, 2),
    '&:first-child': {
      paddingLeft: 0,
    },
    '& + &': {
      borderLeft: `1px solid ${colors.grey[200]}`,
    },
  },
  label: {
    color: colors.grey[500],
    display: 'block',
  },
  value: {
    color: colors.grey[700],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chips: {
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(0.5, 0, 2),
  },
  expandedSection: {
    padding: theme.spacing(2, 0, 2, 4),
    borderTop: `1px solid ${colors.grey[200]}`,
  },
  paramsLabel: {
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${colors.grey[200]}`,
  },
  // Figma: key on the left half, bold value starting at the middle
  paramRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
}))
